import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { filesize } from 'filesize';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import fnolServices from '../../../services/fnolServices';
import { useFnol } from '../../../context';

const FileItem = ({ file, removeFile, doUpload, handleIsButtonDisabled }) => {
  const { fnolTemplate, setFnolTemplate } = useFnol();
  const { t } = useTranslation();
  const progressBar = useRef(null);
  const progressBarWrapper = useRef(null);
  const fileItemWrapper = useRef(null);
  const progress100 = useRef(null);

  const [hasError, setHasError] = useState(false);

  const onUploadProgress = (progressEvent) => {
    const { loaded, total } = progressEvent;
    let percent = Math.floor((loaded * 100) / total);
    if (percent <= 100 && progressBarWrapper.current) {
      progressBar.current.style.width = `${percent}%`;
    }

    if (percent === 100 && progressBarWrapper.current) {
      progressBar.current.style.backgroundColor = '#1ad8a9';
      progress100.current.style.color = '#1ad8a9';
    }
  };

  const uploadFile = async () => {
    handleIsButtonDisabled(true);
    try {
      const s3Url = await fnolServices.getPresignedS3Url(file.name);
      await axios.put(s3Url.data.putFile, file, { onUploadProgress });
      const response = await fnolServices.addAttachment(
        fnolTemplate.fnolKey,
        file.name,
        s3Url.data.getFile
      );
      setTimeout(() => {
        if (progressBarWrapper.current) {
          progressBarWrapper.current.style.maxHeight = 0;
          progressBarWrapper.current.style.marginTop = '-0.25rem';
        }
      }, 2000);
      setFnolTemplate(response.data);
      handleIsButtonDisabled(false);
    } catch (error) {
      setHasError(true);
      handleIsButtonDisabled(false);
    }
  };

  useEffect(() => {
    if (doUpload) {
      uploadFile();
    }
  }, []);

  useEffect(() => {
    if (hasError && fileItemWrapper.current) {
      setTimeout(() => {
        fileItemWrapper.current.style.maxHeight = 0;
        fileItemWrapper.current.style.padding = '0 0.25rem';
        fileItemWrapper.current.style.borderWidth = 0;
        fileItemWrapper.current.style.marginBottom = 0;
        fileItemWrapper.current.style.display = 'none';
      }, 4000);
    }
  }, [hasError]);

  const handleRemoveClick = async () => {
    try {
      await removeFile(file.name);
      fileItemWrapper.current.style.maxHeight = 0;
      fileItemWrapper.current.style.padding = '0 0.25rem';
      fileItemWrapper.current.style.borderWidth = 0;
      fileItemWrapper.current.style.marginBottom = 0;
      fileItemWrapper.current.style.display = 'none';
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <FileItemWrapper ref={fileItemWrapper} hasError={hasError}>
      <FileNameWrapper>
        <FileName>{file.name}</FileName>
        {!hasError && (
          <RemoveFile onClick={handleRemoveClick}>
            {t('uploadFiles.remove')}
          </RemoveFile>
        )}
      </FileNameWrapper>
      {!hasError && doUpload && (
        <ProgressBarWrapper ref={progressBarWrapper}>
          <FlexWrapper>
            {file.size && (
              <FileSize>
                {filesize(file.size, {
                  round: 1,
                  standard: 'jedec',
                  separator: ',',
                })}
              </FileSize>
            )}
            <Wrapper>
              <ProgressBarBackground>
                <ProgressBar ref={progressBar} />
              </ProgressBarBackground>
              <span ref={progress100}>100 %</span>
            </Wrapper>
          </FlexWrapper>
        </ProgressBarWrapper>
      )}
      {hasError && <span>{t('uploadFiles.remove')}</span>}
    </FileItemWrapper>
  );
};

export default FileItem;

const FileItemWrapper = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: ${(props) => props.theme.fileDropArea.file.padding};
  max-height: 7rem;
  overflow: hidden;
  transition: max-height 300ms ease-out, padding 300ms ease-out,
    border-width 50ms ease-out 250ms, margin-bottom 300ms ease-out;
  border: ${(props) => (props.hasError ? '0.063rem solid #fa0000' : 'none')};
  border-radius: ${(props) => props.theme.fileDropArea.file.borderRadius};
  background-color: ${(props) =>
    props.hasError
      ? 'rgba(253, 193, 193, 0.2)'
      : props.theme.fileDropArea.file.backgroundColor};
  margin-bottom: 0.125rem;
`;

const FileSize = styled.span`
  font-weight: 500;
  font-size: 0.75rem;
`;

const FileNameWrapper = styled.div`
  font-weight: 500;
  display: flex;
  justify-content: space-between;
`;

const FileName = styled.span`
  font-size: 0.85rem;
  max-width: 85%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  overflow: hidden;
  max-height: 8rem;
  transition: max-height 500ms ease-out;
  flex-direction: row;

  span {
    font-size: 0.75rem;
  }
`;

const ProgressBarBackground = styled.div`
  background-color: lightgrey;
  flex: 1;
  height: 0.25rem;
`;

const ProgressBar = styled.div`
  background-color: ${(props) => props.theme.fileDropArea.file.active.color};
  height: 100%;
`;

const RemoveFile = styled.button.attrs({ type: 'button' })`
  color: ${(props) => props.theme.errorMessage.fontColor};
  background-color: transparent;
  font-size: 0.75rem;
  cursor: pointer;
  border: none;
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  span {
    margin-left: 0.125rem;
  }
`;
